import { useEffect, useState, type MutableRefObject } from 'react';
import { hiddenTabEdges, scrollLeftToReveal, type HiddenTabEdges } from './tabStripScroll';

export const TAB_KEY_ATTRIBUTE = 'data-tab-key';

const NO_HIDDEN_EDGES: HiddenTabEdges = { start: false, end: false };

function sameEdges(a: HiddenTabEdges, b: HiddenTabEdges): boolean {
    return a.start === b.start && a.end === b.end;
}

function useHiddenTabEdges(stripRef: MutableRefObject<HTMLDivElement | null>): HiddenTabEdges {
    const [edges, setEdges] = useState(NO_HIDDEN_EDGES);
    useEffect(() => {
        const strip = stripRef.current;
        if (!strip) return undefined;
        const update = () => {
            const next = hiddenTabEdges(strip);
            setEdges((prev) => (sameEdges(prev, next) ? prev : next));
        };
        update();
        strip.addEventListener('scroll', update, { passive: true });
        const observer = new ResizeObserver(update);
        observer.observe(strip);
        if (strip.firstElementChild) observer.observe(strip.firstElementChild);
        return () => {
            strip.removeEventListener('scroll', update);
            observer.disconnect();
        };
    }, [stripRef]);
    return edges;
}

function useRevealActiveTab(
    stripRef: MutableRefObject<HTMLDivElement | null>,
    activeKey: string,
    fadePx: number
): void {
    useEffect(() => {
        const strip = stripRef.current;
        const tab = strip?.querySelector<HTMLElement>(
            `[${TAB_KEY_ATTRIBUTE}="${CSS.escape(activeKey)}"]`
        );
        if (!strip || !tab) return;
        const stripLeft = strip.getBoundingClientRect().left;
        const tabRect = tab.getBoundingClientRect();
        const target = scrollLeftToReveal(
            {
                scrollLeft: strip.scrollLeft,
                clientWidth: strip.clientWidth,
                tabLeft: tabRect.left - stripLeft + strip.scrollLeft,
                tabWidth: tabRect.width
            },
            fadePx
        );
        if (target !== null) strip.scrollTo({ left: target, behavior: 'smooth' });
    }, [stripRef, activeKey, fadePx]);
}

// Which ends of an overflowing tab strip hide tabs, keeping the active tab scrolled into view.
export function useTabStripScroll(
    stripRef: MutableRefObject<HTMLDivElement | null>,
    activeKey: string,
    fadePx: number
): HiddenTabEdges {
    useRevealActiveTab(stripRef, activeKey, fadePx);
    return useHiddenTabEdges(stripRef);
}
